import { useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ConnString from '../../ConnString.js';

const Connstring2 = ConnString.connstring;
const passwordPolicyHint = 'Use at least 12 characters with uppercase, lowercase, number, and special character.';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('info');
  const [message, setMessage] = useState('Update your password from account settings.');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      setStatus('error');
      setMessage('All password fields are required.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus('error');
      setMessage('New password and confirm password do not match.');
      return;
    }

    const strongPasswordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;
    if (!strongPasswordPattern.test(newPassword)) {
      setStatus('error');
      setMessage(`Password does not meet complexity requirements. ${passwordPolicyHint}`);
      return;
    }

    if (currentPassword === newPassword) {
      setStatus('error');
      setMessage('New password must be different from current password.');
      return;
    }

    setLoading(true);
    setStatus('info');
    setMessage('Updating password...');

    try {
      const response = await fetch(`${Connstring2}/api/account/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Password updated successfully.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        sessionStorage.removeItem('userPassword');
      } else {
        setStatus('error');
        setMessage(data?.message || 'Unable to update password right now.');
      }
    } catch (err) {
      console.error('Change password failed:', err);
      setStatus('error');
      setMessage('An error occurred while updating your password. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.page}>
      <Paper elevation={10} sx={styles.card}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
          Account Security
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {passwordPolicyHint}
        </Typography>

        <Alert severity={status === 'error' ? 'error' : status} sx={{ mb: 3 }}>
          {message}
        </Alert>

        <Box component="form" onSubmit={handleSubmit} sx={styles.form} noValidate>
          <TextField
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            fullWidth
            autoComplete="current-password"
          />
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
          />

          <Box sx={styles.actions}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Saving...' : 'Save Password'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Backdrop sx={styles.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

const styles = {
  page: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    py: 4,
    px: 2,
  },
  card: {
    width: '100%',
    maxWidth: 620,
    p: 4,
    borderRadius: 3,
  },
  form: {
    display: 'grid',
    gap: 2,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    mt: 1,
  },
  backdrop: {
    color: '#fff',
    zIndex: (theme) => theme.zIndex.drawer + 1,
  },
};

export default ChangePassword;
